import { useMemo } from "react";
import { useLocale } from "@acto/web-common/locales";
import type { DismissalSummary } from "@acto/taskpane/core/domain";
import type { DismissalAnswer } from "@acto/taskpane/core/domain/TeamEmployee";

export interface DismissedCustomerItem {
  id: string;
  date: string;
  clientId: string | null;
  companyName: string | null;
  clientExternalReference: string | null;
  clientAccessible: boolean;
  signalType: string | null;
  answer: DismissalAnswer | null;
  details: {
    location: string | null;
    industry: string | null;
    visitDate: string | null;
    expiryDate: string;
  };
}

export interface DismissedCustomersData {
  employeeName: string;
  dateRange: string;
  ignoredCount: number;
  dismissals: DismissedCustomerItem[];
}

export function useDismissedCustomersData(
  dismissals: DismissalSummary[] | null,
  employeeName: string,
  period: string,
): DismissedCustomersData {
  const { locale } = useLocale();

  return useMemo(() => {
    const dismissalsList = dismissals ?? [];

    const items: DismissedCustomerItem[] = dismissalsList.map((dismissal) => {
      const locationParts = [dismissal.clientCity, dismissal.clientPostcode].filter(Boolean);
      const location = locationParts.length > 0 ? locationParts.join(" ") : null;

      return {
        id: dismissal.id,
        date: dismissal.dismissedAt.locale(locale).format("D MMM"),
        clientId: dismissal.clientId,
        companyName: dismissal.clientName,
        clientExternalReference: dismissal.clientExternalReference,
        clientAccessible: dismissal.clientAccessible,
        signalType: dismissal.observationTitle,
        answer: dismissal.answer,
        details: {
          location,
          industry: dismissal.clientIndustry,
          visitDate: dismissal.lastVisitDate ? dismissal.lastVisitDate.locale(locale).format("D.MMM") : null,
          expiryDate: dismissal.dismissedUntil.locale(locale).format("D.MMM"),
        },
      };
    });

    return {
      employeeName,
      dateRange: period,
      ignoredCount: items.length,
      dismissals: items,
    };
  }, [dismissals, employeeName, period, locale]);
}
